/**
 * WhatsApp deep-link helpers.
 *
 * Uses the wa.me scheme – free, no API key required.
 *   https://wa.me/<international-number>?text=<urlencoded-message>
 *
 * On mobile it opens the WhatsApp app directly with the message pre-filled.
 * On desktop it opens WhatsApp Web. The user still hits "send" themselves,
 * so this is a "compose" link, not a true automated send.
 */

/**
 * Normalize a phone string into the digits-only form wa.me expects.
 * Strips spaces, dashes, parentheses and a leading +.
 * If the number starts with 0 and no country code can be inferred, assume FR (+33).
 */
export function normalizePhoneForWa(raw, defaultCountryCode = "33") {
  if (!raw) return "";
  const digits = String(raw).replace(/[^\d+]/g, "");
  if (!digits) return "";
  if (digits.startsWith("+")) return digits.slice(1);
  if (digits.startsWith("00")) return digits.slice(2);
  if (digits.startsWith("0")) return `${defaultCountryCode}${digits.slice(1)}`;
  return digits;
}

export function buildWaLink(phone, message) {
  const num = normalizePhoneForWa(phone);
  const text = encodeURIComponent(message || "");
  if (!num) return `https://wa.me/?text=${text}`;
  return `https://wa.me/${num}?text=${text}`;
}

/**
 * Build the canonical sanction notification text for a driver.
 * @param {{ driverName?: string, months?: number, monthlyAmount?: number, reason?: string, plate?: string }} opts
 */
export function buildSanctionMessage({ driverName = "", months = 0, monthlyAmount = 0, reason = "", plate = "" }) {
  const lines = [];
  const hi = driverName ? `Bonjour ${driverName},` : "Bonjour,";
  lines.push(hi);
  lines.push("");
  lines.push("Une sanction sur votre prime mensuelle a été enregistrée :");
  if (monthlyAmount > 0 && months > 0) {
    lines.push(`• Montant : -${monthlyAmount.toLocaleString("fr-FR")} € / mois`);
    lines.push(`• Durée : ${months} mois`);
  } else if (months > 0) {
    lines.push(`• Durée : ${months} mois`);
  }
  if (reason && reason.trim()) lines.push(`• Motif : ${reason.trim()}`);
  if (plate) lines.push(`• Camion : ${plate}`);
  lines.push("");
  lines.push("Merci d'accuser réception.");
  lines.push("— NY TRANS");
  return lines.join("\n");
}

/**
 * Build a generic impact notification for the driver (no sanction).
 */
export function buildImpactMessage({ driverName = "", severity = "", description = "", plate = "" }) {
  const lines = [];
  lines.push(driverName ? `Bonjour ${driverName},` : "Bonjour,");
  lines.push("");
  lines.push("Un impact a été enregistré sur ton camion :");
  if (plate) lines.push(`• Camion : ${plate}`);
  if (severity) lines.push(`• Gravité : ${severity}`);
  if (description && description.trim()) lines.push(`• Détail : ${description.trim()}`);
  lines.push("");
  lines.push("Merci de faire attention. — NY TRANS");
  return lines.join("\n");
}
